'use es6';

import { Fragment } from 'react';
import PropTypes from 'prop-types';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UIForm from 'UIComponents/form/UIForm';
import UITextInput from 'UIComponents/input/UITextInput';
import UILink from 'UIComponents/link/UILink';
import Small from 'UIComponents/elements/Small';
import UIAlert from 'UIComponents/alert/UIAlert';
import UIHelpIcon from 'UIComponents/icon/UIHelpIcon';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import UIIcon from 'UIComponents/icon/UIIcon';
import getFormFieldsComponent from './FormFields';
import Toggle from '../Toggle';
import AddField from './AddFieldContainer';
import GDPRSelect from './GDPRSelect';
import KnowledgeBaseButton from 'ui-addon-i18n/components/KnowledgeBaseButton';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const LEARN_MORE_LINK = 'https://knowledge.hubspot.com/forms/prevent-spam-form-submissions';
const i18nKey = key => `ContentComponents.cmv2.fields.form.withInlineEditing.${key}`;
const ReOrderDisabledHelp = () => /*#__PURE__*/_jsx(UIHelpIcon, {
  title: /*#__PURE__*/_jsx(FormattedMessage, {
    message: i18nKey('formContent.topMessage.reOrderingDisabledTooltip')
  }),
  tooltipPlacement: "right"
});
const FormContentTopMessage = ({
  formGuid,
  canReOrder,
  openFormFn
}) => {
  if (!formGuid) {
    return null;
  }
  return /*#__PURE__*/_jsx("p", {
    children: /*#__PURE__*/_jsxs(Small, {
      children: [!canReOrder && /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: i18nKey('formContent.topMessage.reOrderingDisabled_jsx'),
        elements: {
          Help: ReOrderDisabledHelp
        }
      }), /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: i18nKey('formContent.topMessage.moreAdvanced_jsx'),
        options: {
          external: true,
          onClick: openFormFn
        },
        elements: {
          Link: UILink
        }
      })]
    })
  });
};
const FormFieldSection = ({
  DraggableComponents,
  formGuid,
  formDefinition,
  formFields,
  canChangeFieldsOrder,
  onEditFieldClick,
  onRemoveFieldClick,
  onAddFieldClick,
  onChangeFieldsOrder,
  useOnDark,
  openFormFn,
  hasFormsWriteAccess,
  readOnly
}) => {
  const FormFields = getFormFieldsComponent({
    canChangeFieldsOrder,
    DraggableComponents,
    hasFormsWriteAccess,
    readOnly
  });
  return /*#__PURE__*/_jsx("div", {
    children: /*#__PURE__*/_jsx(UIFormControl, {
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('formContent.formFields')
      }),
      children: /*#__PURE__*/_jsxs("div", {
        children: [/*#__PURE__*/_jsx(FormContentTopMessage, {
          formGuid: formGuid,
          canReOrder: canChangeFieldsOrder,
          openFormFn: openFormFn
        }), /*#__PURE__*/_jsx(FormFields, {
          fields: formFields,
          canChangeFieldsOrder: canChangeFieldsOrder,
          onOrderChange: onChangeFieldsOrder,
          onEditClick: onEditFieldClick,
          onRemoveClick: onRemoveFieldClick,
          DraggableComponents: DraggableComponents,
          hasFormsWriteAccess: hasFormsWriteAccess,
          readOnly: readOnly
        }), hasFormsWriteAccess && /*#__PURE__*/_jsx(AddField, {
          formDefinition: formDefinition,
          onSelectProperty: onAddFieldClick,
          useOnDark: useOnDark,
          readOnly: readOnly
        })]
      })
    })
  });
};
const ButtonTextSection = ({
  value,
  onChange,
  useOnDark,
  hasFormsWriteAccess,
  readOnly
}) => /*#__PURE__*/_jsx(UIFormControl, {
  label: /*#__PURE__*/_jsx(FormattedMessage, {
    message: i18nKey('formContent.buttonText')
  }),
  required: true,
  children: /*#__PURE__*/_jsx("div", {
    children: /*#__PURE__*/_jsx(UITextInput, {
      "data-selenium": "form-submit-button-text",
      defaultValue: value,
      onChange: ({
        target: {
          value: newValue
        }
      }) => onChange(newValue),
      use: useOnDark ? 'on-dark' : 'default',
      disabled: !hasFormsWriteAccess,
      readOnly: readOnly
    })
  })
});
const GDPRSection = ({
  value,
  onChange,
  useOnDark,
  disabled,
  readOnly
}) => /*#__PURE__*/_jsx(UIFormControl, {
  label: /*#__PURE__*/_jsx(FormattedMessage, {
    message: i18nKey('formContent.gdprSelect')
  }),
  children: /*#__PURE__*/_jsx(GDPRSelect, {
    value: value,
    useOnDark: useOnDark,
    onChange: onChange,
    disabled: disabled,
    readOnly: readOnly
  })
});
const CaptchaIsEnabledSection = () => /*#__PURE__*/_jsx(UIFormControl, {
  children: /*#__PURE__*/_jsx(UIAlert, {
    type: "info",
    children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      message: i18nKey('formContent.captchaExplaination')
    })
  })
});
const CaptchaLabel = () => /*#__PURE__*/_jsxs(Fragment, {
  children: [/*#__PURE__*/_jsx(FormattedMessage, {
    message: i18nKey('formContent.captcha')
  }), /*#__PURE__*/_jsx(View, {
    marginInlineStart: "100",
    as: "span",
    children: /*#__PURE__*/_jsx(UITooltip, {
      title: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: i18nKey('formContent.captchaTooltip_jsx'),
        options: {
          useZorse: true,
          url: LEARN_MORE_LINK
        },
        elements: {
          Link: KnowledgeBaseButton
        }
      }),
      children: /*#__PURE__*/_jsx(UIIcon, {
        size: 14,
        name: "info"
      })
    })
  })]
});
const CaptchaSection = ({
  value,
  onChange,
  hasFormsWriteAccess,
  readOnly
}) => /*#__PURE__*/_jsxs(Fragment, {
  children: [/*#__PURE__*/_jsx(UIFormControl, {
    children: /*#__PURE__*/_jsx(Toggle, {
      id: "captcha-toggle",
      label: /*#__PURE__*/_jsx(CaptchaLabel, {}),
      value: value,
      onChange: () => onChange(!value),
      disabled: !hasFormsWriteAccess,
      readOnly: readOnly
    })
  }), hasFormsWriteAccess && value ? /*#__PURE__*/_jsx(CaptchaIsEnabledSection, {}) : null]
});
const FormContent = ({
  DraggableComponents = null,
  formGuid = null,
  useOnDark = false,
  buttonText = '',
  isCaptchaEnabled = false,
  formDefinition = null,
  formFields = [],
  canChangeFieldsOrder = true,
  gdprValue = '',
  isGdprSelectDisabled = false,
  hasFormsWriteAccess = true,
  openFormFn = () => {},
  onIsCaptchaEnabledChange = () => {},
  onButtonTextChange = () => {},
  onEditFieldClick = () => {},
  onRemoveFieldClick = () => {},
  onAddFieldClick = () => {},
  onChangeFieldsOrder = () => {},
  onGdprValueChange = () => {},
  readOnly = false
}) => /*#__PURE__*/_jsxs(UIForm, {
  children: [/*#__PURE__*/_jsx(FormFieldSection, {
    DraggableComponents: DraggableComponents,
    formGuid: formGuid,
    formDefinition: formDefinition,
    formFields: formFields,
    canChangeFieldsOrder: canChangeFieldsOrder,
    openFormFn: openFormFn,
    onEditFieldClick: onEditFieldClick,
    onRemoveFieldClick: onRemoveFieldClick,
    onAddFieldClick: onAddFieldClick,
    onChangeFieldsOrder: onChangeFieldsOrder,
    useOnDark: useOnDark,
    hasFormsWriteAccess: hasFormsWriteAccess,
    readOnly: readOnly
  }), /*#__PURE__*/_jsx(ButtonTextSection, {
    value: buttonText,
    onChange: onButtonTextChange,
    useOnDark: useOnDark,
    hasFormsWriteAccess: hasFormsWriteAccess,
    readOnly: readOnly
  }), /*#__PURE__*/_jsx(GDPRSection, {
    value: gdprValue,
    onChange: onGdprValueChange,
    useOnDark: useOnDark,
    disabled: isGdprSelectDisabled || !hasFormsWriteAccess,
    readOnly: readOnly
  }), /*#__PURE__*/_jsx(CaptchaSection, {
    value: isCaptchaEnabled,
    onChange: onIsCaptchaEnabledChange,
    hasFormsWriteAccess: hasFormsWriteAccess,
    readOnly: readOnly
  })]
});
FormContent.propTypes = {
  DraggableComponents: PropTypes.object,
  formGuid: PropTypes.string,
  useOnDark: PropTypes.bool,
  buttonText: PropTypes.string,
  isCaptchaEnabled: PropTypes.bool,
  formDefinition: PropTypes.object,
  formFields: PropTypes.object,
  canChangeFieldsOrder: PropTypes.bool,
  gdprValue: PropTypes.string,
  isGdprSelectDisabled: PropTypes.bool,
  onIsCaptchaEnabledChange: PropTypes.func,
  openFormFn: PropTypes.func,
  onButtonTextChange: PropTypes.func,
  onChangeFieldsOrder: PropTypes.func,
  onEditFieldClick: PropTypes.func,
  onRemoveFieldClick: PropTypes.func,
  onAddFieldClick: PropTypes.func,
  onGdprValueChange: PropTypes.func,
  hasFormsWriteAccess: PropTypes.bool,
  readOnly: PropTypes.bool
};
export default FormContent;